import { memo, useCallback, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import { useUiStore } from "@stores/uiStore";
import { createThemeVariables, isThemeMode } from "@theme/helper";
import { ISystemThemeModeEnum, IThemeModeEnum } from "@theme/types";

import type { PropsWithChildren } from "react";

const documentElement = document.documentElement;

export const ThemeProvider = memo(function ThemeProvider({
  children,
}: PropsWithChildren) {
  const themeMode = useUiStore(useShallow((state) => state.theme));
  const setTheme = useUiStore(useShallow((state) => state.setTheme));

  useEffect(() => {
    if (!isThemeMode(themeMode)) {
      setTheme(IThemeModeEnum.Light);
    }
  }, [setTheme, themeMode]);

  const createCssVariable = useCallback((name: string, value: string) => {
    documentElement.style.setProperty(name, value);
  }, []);

  useEffect(() => {
    createThemeVariables(themeMode, createCssVariable);
  }, [createCssVariable, themeMode]);

  return children;
});

useUiStore.subscribe(
  (state) => state.setting.theme,
  (value) => {
    if (value === ISystemThemeModeEnum.System) {
      useUiStore
        .getState()
        .setTheme(
          globalThis?.matchMedia("(prefers-color-scheme: dark)")?.matches
            ? IThemeModeEnum.Dark
            : IThemeModeEnum.Light,
        );
    } else {
      useUiStore
        .getState()
        .setTheme(
          value === ISystemThemeModeEnum.Dark
            ? IThemeModeEnum.Dark
            : IThemeModeEnum.Light,
        );
    }
  },
);
